import React from 'react';
import {withRouter} from 'react-router';
import {fromJS} from 'immutable';
import {callAction} from 'client/framework';
import Tabs from 'sg/Tabs/Tabs.react';
import {MandarkTable, Column} from 'sg/Tables/MandarkTable/MandarkTable.react';
import {makeLinkRowProps} from 'sg/Tables/Table/table.utils';
import DueDateContent from 'client/Classrooms/shared/DueDateContent/DueDateContent.react';

import SelectableDateRange from 'generic/SelectableDateRange/SelectableDateRange.react';
import {dateRangeOptions, RANGES} from 'generic/SelectableDateRange/shared';
import AssignmentCompletionStatus from 'client/Classrooms/shared/AssignmentCompletionStatus/AssignmentCompletionStatus.react';
import ViewWithSidebar from 'generic/ViewWithSidebar/ViewWithSidebar.react';
import {IE_EXAMS} from 'client/routes';

import sessionStore from 'client/Session/SessionStore';

import studentPracticeExamsListStore from './StudentPracticeExamsList.store';
import studentPracticeExamsListActions from './StudentPracticeExamsList.actions';

import './student-practice-exams-list.scss';

const tabs = fromJS([
  {
    label: 'All',
    badgeContent: () => studentPracticeExamsListStore.getAssignmentCountsByStatus().all,
    query: {
      examStatus: 'all'
    }
  },
  {
    label: 'Open',
    badgeContent: () => studentPracticeExamsListStore.getAssignmentCountsByStatus().open,
    query: {
      examStatus: null
    }
  },
  {
    label: 'Closed',
    badgeContent: () => studentPracticeExamsListStore.getAssignmentCountsByStatus().closed,
    query: {
      examStatus: 'closed'
    }
  }
]);

const StudentPracticeExamsList = ({params}: {params: {classId: string}}) => {
  const {classId} = params;
  const studentId = sessionStore.getUserId();

  const onDateChange = () => {
    const dateRangeStore = studentPracticeExamsListStore
      .getSelectableDateRangeStore()
      .getDateRangeStore();
    if (dateRangeStore.isValid()) {
      callAction(studentPracticeExamsListActions.SET_LAST_VALID_DATES, {
        startDate: dateRangeStore.getStartDate(),
        endDate: dateRangeStore.getEndDate()
      });
    }
  };

  const getExamLinkProps = makeLinkRowProps((exam) =>
    exam.getSections().isEmpty()
      ? `/assignment/${exam.getId()}`
      : IE_EXAMS.getRouteForExam(exam.getId())
  );

  if (!process.env.IS_BROWSER) {
    return null;
  }

  const examsQuery = studentPracticeExamsListStore.getStudentPracticeExamsQuery();

  return (
    <ViewWithSidebar
      sidebarItems={[
        {
          label: 'Filters',
          content: (
            <SelectableDateRange
              storeName={studentPracticeExamsListStore.getSelectableDateRangeStore().getName()}
              className='student-practice-exams-list__date-picker card'
              options={dateRangeOptions}
              onDateChange={onDateChange}
              defaultDateRange={RANGES.ALL_TIME}
              endDateLabel='End'
              label='Due Date'
              startDateLabel='Start'
            />
          )
        }
      ]}
      mainItems={[
        {
          label: 'My Assessments',
          content: (
            <Tabs content={tabs}>
              <MandarkTable
                mobileBreakpoint={700}
                query={examsQuery}
                storeName='StudentPracticeExamsListTableStore'
                rowPropsFunc={getExamLinkProps}
              >
                <Column
                  defaultSortParam
                  heading='Due Date'
                  sortBy='due_date'
                  content={(exam) => (
                    <DueDateContent assignment={exam} classroomId={classId} studentId={studentId} />
                  )}
                />
                <Column heading='Assessment' sortBy='name' content={(exam) => exam.getName()} />
                <Column
                  heading='Questions'
                  sortBy='meta.count_of_questions'
                  content={(exam) => exam.getMeta().getCountOfQuestions()}
                />
                <Column
                  heading='Performance'
                  content={(exam) => {
                    if (
                      !exam.shouldShowGuessFeedback() ||
                      !exam.getMeta().getStudentCountOfGuesses()
                    ) {
                      return null;
                    }
                    const numberAnswered = exam.getMeta().getStudentCountOfGuesses();
                    const correctCount = exam.getMeta().getStudentCountOfCorrectGuesses();
                    const incorrectCount = numberAnswered - correctCount;
                    const questionCount = exam.getMeta().getCountOfQuestions();
                    return (
                      <AssignmentCompletionStatus
                        tiny
                        numberSubmitted={correctCount}
                        numberSubmittedLate={incorrectCount}
                        numberNotSubmitted={questionCount - numberAnswered}
                      />
                    );
                  }}
                />
              </MandarkTable>
            </Tabs>
          )
        }
      ]}
    />
  );
};

export default withRouter(StudentPracticeExamsList);
